import type { ActivityData } from '@/types/websocket';
import { ActivityStatusValues } from '@/types/websocket';

/**
 * How long an activity may keep claiming IN_PROGRESS before we stop believing it.
 *
 * An activity record is opened by the worker that starts the job and closed by
 * that same worker. When the worker dies without reaching its own error path —
 * an OOM kill, a lambda timeout, a deploy mid-job — nothing ever writes the
 * terminal status and the record claims IN_PROGRESS forever. The editor only
 * reads activities (`GET /user/{id}/activities`, there is no write endpoint),
 * so it cannot repair such a record; it has to stop trusting it instead.
 *
 * Six hours is a deliberate over-estimate, not a measurement. The longest job
 * of any kind is the autocourse lambda, hard-capped at 15 minutes by its own
 * timeout, whose slowest successful real run measured 11m55s; copy, publish,
 * translate and voice sync all finish far inside that. So nothing legitimately
 * in flight can reach this bound, while an orphan clears on the next update.
 */
const ACTIVITY_STALE_AFTER_MS = 6 * 60 * 60 * 1000;

/**
 * Whether an activity is plausibly still running right now.
 *
 * Use this rather than a bare `status === IN_PROGRESS` for every ambient
 * "something is happening" affordance — notification dots, spinners, the
 * cross-space generation banner. Those read the status as a live fact, so a
 * single orphaned record pins them on permanently: a generation OOM-killed on
 * pippin on 2026-08-07 was still lighting the banner in every other space a
 * week later.
 *
 * The activity list itself is exempt on purpose. It reports history, so it
 * keeps showing whatever status the backend actually holds rather than one
 * inferred here.
 *
 * Reads the clock directly, as `formatRelative` alongside it does, so callers
 * in a render pass do not have to thread a timestamp through. Fake timers in
 * tests.
 */
export function isActivityRunning(activity: ActivityData) {
  if (activity.status !== ActivityStatusValues.IN_PROGRESS) {
    return false;
  }
  // A finished timestamp alongside IN_PROGRESS is a half-written record, which
  // means the job is over however the status reads.
  if (activity.finishedAt !== null) {
    return false;
  }
  const startedAt = new Date(activity.startedAt).getTime();
  if (Number.isNaN(startedAt) === true) {
    return false;
  }
  return Date.now() - startedAt < ACTIVITY_STALE_AFTER_MS;
}
